import React, { useState } from 'react';
import axios from 'axios';
import { Container, TextField, Button, MenuItem, Select, FormControl, InputLabel, CircularProgress, Typography, Box } from '@mui/material';
import PropagateLoader from "react-spinners/PropagateLoader";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import icon from './saveicon.png';
import './freeChartBuilder.css';

function DataGenerator() {
  const [query, setQuery] = useState('');
  const [dataset, setDataset] = useState([]);
  const [debug, setDebug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState(''); // Categories state
  const [numRows, setNumRows] = useState(15); // Number of rows state
  const [numCols, setNumCols] = useState(3); // Number of columns state
  const [numCategories, setNumCategories] = useState(''); // Number of categories state, defaulting to 2
  const [clickCount, setClickCount] = useState(0); // Click counter state

  const handleDClick = () => {
    setClickCount(prevCount => prevCount + 1);
  };

  const datagenerator = async () => {
    try {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      setDataset([]);
      setDebug(''); // debug

      // Include the categories, number of rows, number of columns, and number of categories in the query
      const enhancedQuery = `${query} Categories: ${categories === 'true' ? (numCategories || 'Yes, GPT you will decide how many categories') : categories}, Rows: ${numRows}, Columns: ${numCols}`;

      const response = await axios.post('https://us-central1-explo-website-tools.cloudfunctions.net/generateData', { query: enhancedQuery });
      setDataset(response.data.dataset);
      setDebug(response.data.debug); // debug
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
    }
  };

  const renderTable = (data) => {
    if (!data.length) return null;

    const columns = Object.keys(data[0]);
    return (
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((col) => (
                <td key={col}>{row[col]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const exportToCSV = () => {
    const rows = [Object.keys(dataset[0]), ...dataset.map(row => Object.values(row))];
    const csvContent = rows.map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "dataset.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container className="app-container">
      <Typography variant="h4">
        <span onClick={handleDClick} style={{ cursor: 'pointer' }}>D</span>ata Generator
      </Typography>
      <Box className="query-input" mt={2}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          placeholder="Provide instructions of the type of dataset you want. (i.e. 'Give me a mock dataset of a social media company' or 'Give me a dataset of names, ages, and float values from 0 to 9000 with 50 rows')"
        />
        <Box display="flex" alignItems="center" mt={2}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Categories</InputLabel>
            <Select
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              label="Categories"
            >
              <MenuItem value="">AI decision</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Dataset will be grouped into different categories relevant to the prompt. Choose 'AI decision' to let AI decide, 'Yes' to include categories, or 'No' to exclude categories. Try enabling categories, and leaving '# of categories' blank for AI to take care of the details!" placement="right">
            <HelpOutlineIcon sx={{ fontSize: 20, marginLeft: '5px' }} />
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="center" mt={2}>
          <TextField
            type="number"
            label="# of rows (1 - 100,000)"
            value={numRows === '' ? '' : numRows}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Math.min(Number(e.target.value), 100000);
              setNumRows(value);
            }}
            variant="outlined"
            style={{ marginRight: '10px' }}
          />
          <TextField
            type="number"
            label="# of columns (1 - 10)"
            value={numCols === '' ? '' : numCols}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Math.min(Number(e.target.value), 10);
              setNumCols(value);
            }}
            variant="outlined"
          />
        </Box>
        {categories === 'true' && (
          <Box display="flex" alignItems="center" mt={2}>
            <TextField
              type="number"
              label="# of categories"
              value={numCategories}
              onChange={(e) => setNumCategories(e.target.value)}
              placeholder="blank for AI decision"
              variant="outlined"
            />
          </Box>
        )}
        <Button variant="contained" color="primary" onClick={datagenerator} style={{ marginTop: '20px' }}>
          Generate Dataset
        </Button>
        {isLoading && 
          <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <PropagateLoader color="#3C66DD" />
          </Box>
        }
        {clickCount >= 5 && (
          <Typography variant="body1" mt={2}>
            Debug information:<br />
            User query- {query}<br />
            GPT output- {debug}
          </Typography>
        )}
      </Box>
      {dataset.length > 0 && (
        <Box className="dataset-output" mt={4}>
          <Button
            variant="contained"
            color="secondary"
            onClick={exportToCSV}
            style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 10px' }}
          >
            <img src={icon} alt="Export CSV" className="export-icon" style={{ marginRight: '8px' }} />
            Export as CSV
          </Button>
          {renderTable(dataset)}
        </Box>
      )}
    </Container>
  );
}

export default DataGenerator;